import { Injectable, Injector } from '@angular/core';
import { DatabaseManagerProvider } from '../storage-manager/database-manager';
import { Chat } from '../../bayadic-shared-models/chat/Chat';
import { AbstractChatMessage } from '../../bayadic-shared-models/classes/message/abstract-chat-message';
import { ChatMessage } from '../../bayadic-shared-models/classes/message/chat-message';
import { MessageData } from '../../bayadic-shared-models/classes/message/message-data';
import { ITEM_TYPES } from '../../bayadic-shared-models/item/item-types';
import { HelperService } from '../../bayadic-shared-models/helper-service';
import { LoggerService } from '../../app/services/logger.service';
@Injectable({
  providedIn: 'root'
})
export class ChatsDatabaseService extends DatabaseManagerProvider {
  isOpeningDatabase: boolean = false;
  currentCursor: IDBCursorWithValue;

  messageDatabaseUpgrader: IDBDatabase;
  messageDatabase: IDBDatabase;
  messageDBName: string;
  constructor(public injector: Injector,
    public logger: LoggerService
  ) {
      super(injector, logger)
  }
  async deleteMessage(chat: Chat, message: AbstractChatMessage) {
    return new Promise((resolve, reject) => {
      const objectStore = this.initializeTransaction(chat.chatID, "readwrite")
      const deleteRequest = objectStore.delete(message.messageID);
      deleteRequest.onsuccess = () => {
        this.logger.debug("Message deleted successfully", message);
        resolve(true);
      };
      deleteRequest.onerror = (event: Event) => {
        console.warn("Error deleting message", event);
        resolve(false);
      };
    });
  }
  readPagesOfTodayScheduledMessage(chat: Chat, pageSize: number = 10, pageNumber: number = 1): Promise<MessageData<any>[]> {
    if (!this.messageDatabase) throw new Error("No database initialized");
    return new Promise(async (resolve, reject) => {
      const storeName = chat.chatID;
      const objectStore = this.initializeTransaction(storeName, "readonly")
      const timestampIndex = objectStore.index('scheduledTimeIndex');
      const currentDate = new Date();
      const yesterday = new Date(currentDate);
      yesterday.setDate(currentDate.getDate() - 1);
      const tomorrow = new Date(currentDate);
      tomorrow.setDate(currentDate.getDate() + 1);
      const idbKeyRange = IDBKeyRange.bound(yesterday, tomorrow);
      const cursorRequest = timestampIndex.openCursor(idbKeyRange, "prev");
      const messages: MessageData<any>[] = await this.fetchMessages(cursorRequest, pageSize, pageNumber)
      resolve(messages)
    });
  }

  initializeTransaction(storeName: string, mode: 'readwrite' | 'readonly'): IDBObjectStore | null {
    if (!this.messageDatabase) throw new Error("No database initialized");
    // Check if the object store exists before attempting to access it
    if (!this.messageDatabase.objectStoreNames.contains(storeName)) {
      console.warn(`Object store "${storeName}" not found in the database.`);
      throw new Error(`Object store "${storeName}" not found in the database.`);
      return null;
    }
  
    const transaction = this.messageDatabase.transaction(storeName, mode);
    const objectStore = transaction.objectStore(storeName);
    return objectStore;
  }
  async fetchMessages(cursorRequest: IDBRequest<IDBCursorWithValue>, pageSize: number, pageNumber: number): Promise<MessageData<any>[]> {
    const messages: MessageData<any>[] = [];
    let count = 0;

    return new Promise((resolve, reject) => {
      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          if (count >= (pageNumber - 1) * pageSize && count < pageNumber * pageSize) {
            if (cursor.value) {
              const validatedMessage = this.validate(cursor.value);
              if (validatedMessage) {
                messages.unshift(cursor.value);
              }
            }
          }
          if (count === pageNumber * pageSize) {
            resolve(messages);
            return;
          }
          cursor.continue();
          count++;
        } else {
          resolve(messages);
        }
      };

      cursorRequest.onerror = (event: any) => {
        reject(event.error);
      };
    });
  }

  addMessageAtEnd(chat: Chat, message: AbstractChatMessage) {
    //return true;
    return new Promise((resolve, reject) => {
      const transaction = this.messageDatabase.transaction(chat.chatID, 'readwrite');
      const objectStore = transaction.objectStore(chat.chatID);

      const cursorRequest = objectStore.openCursor(null, 'prev'); // Open cursor at the end

      cursorRequest.onsuccess = event => {
        const cursor = (event.target as IDBRequest).result;

        if (cursor) {
          // Cursor is at the end, so you can add the message here
          const addRequest = cursor.source.add(message);

          addRequest.onsuccess = () => {
            this.logger.debug('Message added to the database at the end');
            resolve(true);
          };

          addRequest.onerror = (event: any) => {
            console.warn('Error adding message to the database:', event);
            resolve(false);
          };
        } else {
          // If there are no messages in the store yet, add the message directly
          const addRequest = objectStore.add(message);

          addRequest.onsuccess = () => {
            this.logger.debug('Message added to the empty database');
            resolve(true);
          };

          addRequest.onerror = event => {
            console.warn('Error adding message to the database:', event);
            resolve(false);
          };
        }
      };

      cursorRequest.onerror = () => {
        console.warn('Error opening cursor:', cursorRequest.error);
        resolve(false);
      };
    });
  }
  async addMessage(chat: Chat, message: MessageData<any>) {
    //return true;
    return new Promise(async (resolve, reject) => {
      const transaction = this.messageDatabase.transaction(chat.chatID, 'readwrite');
      const objectStore = transaction.objectStore(chat.chatID);
      const addRequest = objectStore.add(message); // Open cursor at the end
      addRequest.onsuccess = event => {
        const cursor = (event.target as IDBRequest).result;
        resolve(true)
      };
      addRequest.onerror = () => {
        console.warn('Error adding message:', addRequest.error);
        resolve(false);
      };
    });
  }

  async addMessagesBulk(chat: Chat, messages: MessageData<any>[]) {
    if (messages.length === 0) return;
    return new Promise<void>((resolve) => {
      const transaction = this.messageDatabase.transaction(chat.chatID, 'readwrite');
      const objectStore = transaction.objectStore(chat.chatID);
      for (const message of messages) {
        objectStore.add(message);
      }
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => {
        console.warn('Error bulk adding messages:', transaction.error);
        resolve();
      };
    });
  }
  readScheduledBeforeToday(
    chat: Chat, pageSize: number = 10,
    pageNumber: number = 1
    ): Promise<MessageData<any>[]> {
    if (!this.messageDatabase) throw new Error("no database initilaized")
    return new Promise(async (resolve, reject) => {
      const objectStore = this.initializeTransaction(chat.chatID, "readonly")
      const timestampIndex = objectStore.index('scheduledTimeIndex');
      let idbKeyRange = null;
      const date = this.getLastScheduledDate(chat);
      idbKeyRange = IDBKeyRange.upperBound(date);
      const cursorRequest = timestampIndex.openCursor(idbKeyRange, "prev");
      const messages: MessageData<any>[] = await this.fetchMessages(cursorRequest, pageSize, pageNumber)

      resolve(messages);
    });
  }
  private getLastScheduledDate(chat: Chat) {
    let date = new Date();
    if (chat.scheduledMessages?.length > 0) {
      let date = chat.scheduledMessages[0]?.scheduledTime;
      if (typeof date === typeof "string") {
        date = new Date(date);
      }
    }
    // date.setMilliseconds(date.getMilliseconds() + 1);
    // set date to tomorrow
    date.setDate(date.getDate());
    return date;
  }

  readPagesOfMessage(chat: Chat, pageSize: number = 10, pageNumber: number = 1): Promise<MessageData<any>[]> {
    if (!this.messageDatabase) throw new Error("no database initilaized")
    return new Promise(async (resolve, reject) => {
      const objectStore = this.initializeTransaction(chat.chatID, "readonly")
      const timestampIndex = objectStore.index('timeIndex');
      let idbKeyRange = null;
      if (chat.messages?.length > 0) {
        let date = this.getLastLoadedMessageDate(chat);
        date.setMilliseconds(date.getMilliseconds() - 1000);
        idbKeyRange = IDBKeyRange.upperBound(date);
      }
      const cursorRequest = timestampIndex.openCursor(idbKeyRange, "prev");
      const messages: MessageData<any>[] = await this.fetchMessages(cursorRequest, pageSize, pageNumber)

      resolve(messages);
    });
  }
  private getLastLoadedMessageDate(chat: Chat) {
    let date = chat.messages[0]?.timestamp;
    if (typeof date === typeof "string") {
      date = new Date(date);
    }
    this.logger.debug("date", date);
    return date;
  }

  private validate(value: MessageData<any>): false | MessageData<any> {
    let wrongValue = null;
    if (!value) wrongValue = "object"
    if (!value.messageID) wrongValue = "messageID"
    if (!value.messageType) wrongValue = "messageType"
    if (!value.timestamp) wrongValue = "timestamp"
    if (!value.content) wrongValue = "content"
    if (wrongValue) {
      console.warn("wrong ", wrongValue)
      throw new Error("not valid because of " + wrongValue)
    }
    return {
      content: value.content,
      from: value.from || "",
      messageID: value.messageID,
      messageType: value.messageType,
      received: true,
      itemID: "",
      receivedTime: "",
      timestamp: new Date(value.timestamp),
      scheduledTime: new Date(value.scheduledTime)
    }
  }
  async updateMessageByChat(chat: Chat, message: AbstractChatMessage): Promise<boolean> {
    return this.updateMessage(message);
  }

  public async updateMessage(message: AbstractChatMessage): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      await this.createTablesIfNotExists(message.chatID);
      this.logger.debug("it has database table ", message.chatID);
      const objectStore = this.initializeTransaction(message.chatID, "readwrite");
      const putRequest = objectStore.put(message);
      putRequest.onsuccess = () => {
        this.logger.debug("Message updated successfully", message);
        resolve(true);
      };
      putRequest.onerror = (event: Event) => {
        console.warn("Error updating message", event);
        reject(false);
      };
    });
  }

  async getCurrentDatabaseVersion(dbName: string): Promise<number> {
    //it does not work in firefox
    //https://stackoverflow.com/questions/69118042/if-there-is-any-function-in-firefox-to-get-all-indexeddb-names
    const databastes = await indexedDB.databases()
    const db = databastes.filter(it => it.name === dbName)
    if (db.length > 0) return db[0].version; else return 0;
  }
  async closeDatabase() {
    if (this.messageDatabase) {
      this.messageDatabase.close()
    }
  }
  async createTablesIfNotExists(chatID: string): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        this.messageDBName = "messages_" + this.userManager.user.userID;
        if (!this.userManager.user.userID) {
          throw new Error("No user ID available");
        }

        // Check if table already exists
        if (await this.hasTable(chatID)) {
          resolve(true);
          return;
        }

        // Get current database version
        this.databaseVersion = await this.getCurrentDatabaseVersion(this.messageDBName);
        
        // Close existing database connection if any
        if (this.messageDatabase) {
          this.messageDatabase.close();
        }

        // Open database with incremented version
        const request = indexedDB.open(this.messageDBName, this.databaseVersion + 1);

        request.onerror = (event: Event) => {
          console.error("Error opening database:", event);
          reject(new Error("Failed to open database"));
        };

        request.onsuccess = (event: Event) => {
          const db = (event.target as IDBRequest).result as IDBDatabase;
          this.messageDatabase = db;
          resolve(true);
        };

        request.onupgradeneeded = (event: Event) => {
          const db = (event.target as IDBRequest).result as IDBDatabase;
          
          // Create object store if it doesn't exist
          if (!db.objectStoreNames.contains(chatID)) {
            const objectStore = db.createObjectStore(chatID, { keyPath: "messageID" });
            objectStore.createIndex("timeIndex", "timestamp", { unique: false });
            objectStore.createIndex("scheduledTimeIndex", "scheduledTime", { unique: false });
            this.logger.debug(`Created object store for chat ${chatID}`);
          }
        };
      } catch (error) {
        console.error("Error in createTablesIfNotExists:", error);
        reject(error);
      }
    });
  }
  async deleteTable(chatID: string): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        if (this.messageDatabase.objectStoreNames.contains(chatID)) {
          this.messageDatabase.close();
          const version = this.messageDatabase.version + 1;
          const upgradeRequest = indexedDB.open(this.messageDBName, version);
  
          upgradeRequest.onupgradeneeded = (event: IDBVersionChangeEvent) => {
            const upgradedDB = (event.target as IDBRequest).result as IDBDatabase;
  
            if (upgradedDB.objectStoreNames.contains(chatID)) {
              upgradedDB.deleteObjectStore(chatID);
            }
          };
  
          upgradeRequest.onsuccess = () => {
            this.logger.debug(`Table "${chatID}" deleted successfully.`);
            resolve(true);
          };
  
          upgradeRequest.onerror = (event: Event) => {
            console.warn(`Error deleting table "${chatID}":`, event);
            resolve(false);
          };
        } else {
          this.logger.debug(`Table "${chatID}" does not exist.`);
          resolve(true); // Resolve as true because the table doesn't exist.
        }
      } catch (error) {
        console.warn("Error deleting table:", error);
        resolve(false);
      }
    });
  }
  
  async hasTable(chatID: string): Promise<boolean> {
    this.logger.debug("has table?", chatID)
    if(this.isOpeningDatabase) {
      await HelperService.waitForTimeout(10000)
      this.logger.debug("has table? looking in store names", chatID)
    }
    this.isOpeningDatabase = true;
    if(this.messageDatabase) {
      this.logger.debug("has table? looking in store names", chatID)
      this.isOpeningDatabase = false;

      return this.messageDatabase.objectStoreNames.contains(chatID)
    }
    else {
      this.logger.debug("has table? creating new connection", chatID)
    return new Promise((resolve, reject) => {
      this.logger.debug("has table? opening table", this.messageDBName)
      // this.closeDatabase()
      const request = indexedDB.open(this.messageDBName);

      request.onsuccess = (event: Event) => {
        const dbRequest = (event.target as IDBRequest).result as IDBDatabase;
        this.messageDatabase = dbRequest;
        this.logger.debug("has table? looking in on success", chatID)
        // this.closeDatabase()

        if (dbRequest.objectStoreNames.contains(chatID)) {
          this.logger.debug("resolved true")
          this.isOpeningDatabase = false;

          resolve(true)
        }
        else {
          this.logger.debug("resolved false")
          resolve(false)
        }
      }
      request.onerror = (event: Event) => {
        this.logger.debug("has table? error", event)
        resolve(false)
      }
    })
  }
  }
  readMessageById(chat: Chat, message: AbstractChatMessage): Promise<ChatMessage | false> {
    return new Promise((resolve, reject) => {
      const transaction = this.messageDatabase.transaction(chat.chatID, "readonly");
      const objectStore = transaction.objectStore(chat.chatID);
      const request = objectStore.get(message.messageID);

      request.onsuccess = (event) => {
        const foundMessage = (event.target as IDBRequest).result;
        if (foundMessage) {
          resolve(foundMessage)
        } else {
          this.logger.debug("Message not found with ID:", foundMessage);
          resolve(false)
        }
      };

      request.onerror = (event) => {
        console.warn("Error reading message:", (event.target as IDBRequest).error);
      };
    })
  }
  readLastMessage(chat: Chat): Promise<ChatMessage | false> {
    return new Promise((resolve, reject) => {
      const transaction = this.messageDatabase.transaction(chat.chatID, "readonly");
      const objectStore = transaction.objectStore(chat.chatID);
      const timestampIndex = objectStore.index('timeIndex');

      const request = timestampIndex.openCursor(null, "prev");

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const lastMessage = cursor.value;
          this.logger.debug("Last message found:", lastMessage);
          resolve(lastMessage);
        } else {
          this.logger.debug("No last message found.");
          resolve(false);
        }
      };

      request.onerror = (event) => {
        console.warn("Error reading last message:", (event.target as IDBRequest).error);
        resolve(false)
      };
    });
  }
  readFirstMessage(chat: Chat): Promise<ChatMessage | false> {
    return new Promise((resolve, reject) => {
      const transaction = this.messageDatabase.transaction(chat.chatID, "readonly");
      const objectStore = transaction.objectStore(chat.chatID);
      const timestampIndex = objectStore.index('timeIndex');

      const request = timestampIndex.openCursor();

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const firstMessage = cursor.value;
          this.logger.debug("First message found:", firstMessage);
          resolve(firstMessage);
        } else {
          this.logger.debug("No first message found.");
          resolve(false);
        }
      };

      request.onerror = (event) => {
        console.warn("Error reading first message:", (event.target as IDBRequest).error);
      };
    });
  }

  prependMessage(chat: Chat, message: AbstractChatMessage) {
    return new Promise((resolve, reject) => {
      const transaction = this.messageDatabase.transaction(chat.chatID, "readwrite");
      const objectStore = transaction.objectStore(chat.chatID);
      // const keyRange = IDBKeyRange.only(chat.messages[0].messageID);
      let cursorRequest = null;
      cursorRequest = objectStore.openCursor(null, "next");
      cursorRequest.onsuccess = (event: { target: IDBRequest<any>; }) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue;
        if (cursor) {
          cursor.continue();
        } else {
          // If cursor reaches the end, prepend the message
          this.logger.debug("message", message.content)
          const addRequest = objectStore.add(message); // Use message ID as the key
          addRequest.onsuccess = () => {
            this.logger.debug("Message prepended to the database");
            resolve(true);
          };
          addRequest.onerror = (event: Event) => {
            console.warn("Error prepending message to the database:", (event.target as IDBRequest).error);
            resolve(false);
          };
        }
      };

      cursorRequest.onerror = (event: Event) => {
        console.warn("Error opening cursor:", event);
        resolve(false);
      };
    });

  }

  async init() {
    const user = this.userManager.user;
  }


}
